import React from "react";
import { useTranslation } from "react-i18next";
import {
  TableRow,
  TableCell,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import { IChangeReview } from "../../../../types/review";
import { processCarWashReview } from "../../../../services/carWash.service";
import ChangeReviewDiffModal from "./ChangeReviewDiffModal";
import AssignRMModal from "./AssignRMModal";

type Props = {
  item: IChangeReview;
  onActionComplete: () => void;
  onError: (message: string) => void;
};

type CountdownInfo = {
  label: string;
  color: "success" | "warning" | "error" | "default";
};

// Same countdown logic as RMPendingReviewRow (Phase 28 D-04): deadline is
// createdAt + 72h. Color thresholds: green >= 12h remaining, orange 4-12h,
// red < 4h or expired.
function computeCountdown(
  t: (key: string, opts?: Record<string, unknown>) => string,
  item: IChangeReview,
  nowMs: number
): CountdownInfo {
  if (!item.createdAt) {
    return { label: "—", color: "default" };
  }
  const base = new Date(item.createdAt).getTime() + 72 * 3600 * 1000;
  if (Number.isNaN(base)) {
    return { label: "—", color: "default" };
  }

  const remainingMs = base - nowMs;
  if (remainingMs <= 0) {
    return { label: t("manageCarWashes.expired"), color: "error" };
  }

  const totalMinutes = Math.floor(remainingMs / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  let color: CountdownInfo["color"];
  if (h >= 12) {
    color = "success";
  } else if (h >= 4) {
    color = "warning";
  } else {
    color = "error";
  }

  return { label: t("manageCarWashes.countdownRemaining", { hours: h, minutes: m }), color };
}

// The populated owner may be an object (populated) or a string (id).
function ownerLabel(owner: IChangeReview["owner"]): string {
  if (!owner || typeof owner === "string") return "—";
  const name = `${owner.firstName ?? ""} ${owner.lastName ?? ""}`.trim();
  return name || "—";
}

function ownerPhone(owner: IChangeReview["owner"]): string {
  if (!owner || typeof owner === "string") return "";
  if (!owner.phone) return "";
  return `${owner.country_code ?? ""} ${owner.phone}`.trim();
}

// Display-only fallback. carWash can legitimately be null/undefined for
// create-type reviews (no CarWash document exists yet) — that is NOT an
// error state, just nothing to show.
function carWashLabel(carWash: IChangeReview["carWash"]): string {
  if (!carWash || typeof carWash === "string") return "—";
  return carWash.legalName || "—";
}

// NOTE: deliberately no `carWashId()` helper here and no pre-flight guard on
// it. RMPendingReviewRow derives a carWashId from item.carWash and refuses to
// act when it's absent — which breaks approving brand-new car-wash creation
// requests (carWash: null). This component acts on item._id (the review id)
// only, via processCarWashReview, so create-type reviews work correctly.
const PendingCarWashReviewRow = ({ item, onActionComplete, onError }: Props) => {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  // Tick once a minute so the countdown stays roughly current without churn.
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const countdown = computeCountdown(t, item, nowMs);

  // Stop row-click when interacting with action buttons.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const runAction = async (status: "approved" | "rejected") => {
    if (!item._id) {
      onError(t("manageCarWashes.missingReviewReference"));
      return;
    }
    setBusy(true);
    try {
      await processCarWashReview(item._id, status, undefined);
      onActionComplete();
    } catch (err: any) {
      onError(
        err?.message ||
          (status === "approved"
            ? t("serviceErrors.acceptChangeRequest")
            : t("serviceErrors.rejectChangeRequest"))
      );
      // Reconcile on failure too — see RMPendingReviewRow for why.
      onActionComplete();
    } finally {
      setBusy(false);
    }
  };

  const phone = ownerPhone(item.owner);
  // Assign RM (260801-sa1) operates on the owner, not the review — extract
  // the owner id defensively since item.owner may be populated or a string.
  const ownerId =
    typeof item.owner === "string" ? item.owner : item.owner?._id;
  // The owner's current relationshipManager (260801-sa2) — same defensive
  // extraction: if owner is a string (unpopulated), there's no nested
  // relationshipManager to show.
  const currentRM =
    typeof item.owner === "string" ? null : item.owner?.relationshipManager ?? null;

  return (
    <>
      <TableRow hover tabIndex={-1}>
        <TableCell align="left">{carWashLabel(item.carWash)}</TableCell>
        <TableCell align="left">
          {ownerLabel(item.owner)}
          {phone ? ` (${phone})` : ""}
        </TableCell>
        <TableCell align="left">
          <Chip
            data-testid="countdown-chip"
            label={countdown.label}
            color={countdown.color}
            size="small"
          />
        </TableCell>
        <TableCell align="left" onClick={stop}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={t("manageCarWashes.viewChanges")}>
              <IconButton size="small" onClick={() => setModalOpen(true)}>
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={busy || !ownerId}
              onClick={() => setAssignModalOpen(true)}
            >
              {t("manageCarWashes.assignRM")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={busy}
              onClick={() => runAction("rejected")}
            >
              {t("common.reject")}
            </Button>
          </Stack>
        </TableCell>
      </TableRow>

      {modalOpen && (
        <ChangeReviewDiffModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          review={item}
          onAccept={() => runAction("approved")}
          onReject={() => runAction("rejected")}
        />
      )}

      <AssignRMModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        ownerId={ownerId ?? ""}
        onAssigned={onActionComplete}
        onError={onError}
        currentRM={currentRM}
      />
    </>
  );
};

export default PendingCarWashReviewRow;
